<template>
  <div data-module="work-image-text-column">
    <div class="module-layout">

      <!-- container for all image content -->
      <div class="image-container"
        v-if="hasImage"
        :class="imageContainerClasses"
        ref="imageContainer"
      >
        <div class="image" :style="imageStyles" />
        <div v-if="imageCaption" class="caption">{{ imageCaption }}</div>
      </div>

      <!-- container for text content -->
      <div class="content-container"
        v-if="hasContent"
        :class="contentContainerClasses"
      >
        <markdown class="markdown">{{contentText}}</markdown>
      </div>
    </div>
  </div>
</template>

<script>
/*
  Work section ImageTextColumn module

  data record:
  {
    reversed: <boolean>
    image: {
      height: <string>, // 'short', 'medium', 'tall'
      src: <url> //
      alt: <string>, // 'picture of a big mouse'
      caption: <string>, // 'a big mouse'
      hAlign: <string>, // 'left', 'center', 'right'
      vAlign: <string>, // 'top', 'center', 'bottom'
    },
    content: {
      text: <markdown>,
      hAlign: <string> // 'left' (default), 'right'
    }
  }
*/
import Vue from 'vue';

// Utils
import { getResponsiveUrl } from '@/utils/image-utils';

import WorkMixin from './WorkMixin';

export default {

  name: 'work-image-text-column',

  props: ['data'],

  mixins: [WorkMixin],

  data() {
    return {
      imageContentHeight: 0,
      imageContentWidth: 0,
    };
  },

  computed: {

    hasContent() {
      const { content } = this.data;
      return (content !== undefined);
    },

    hasImage() {
      const { image } = this.data;
      return (image !== undefined);
    },

    imageCaption() {
      const { image } = this.data;
      return image.caption;
    },

    contentText() {
      const { content } = this.data;
      return content.text;
    },

    containerClasses() {
      // return class string, eg 'desktop', 'mobile.reversed'
      const isReversed = this.isReversed;

      let deviceStr = '';
      if (this.isMobile) {
        deviceStr = 'mobile';
      } else if (this.isTablet) {
        deviceStr = 'tablet';
      } else {
        deviceStr = 'desktop';
      }
      return (isReversed) ? `${deviceStr} reversed` : deviceStr;
    },

    contentContainerClasses() {
      // return class string, eg 'desktop', 'mobile.reversed'
      const { data } = this;
      const classStr = this.containerClasses;
      const { content } = data;
      const { hAlign: hA } = content;

      // add defaults
      const hAlign = hA || 'left';

      return `${classStr} ${hAlign}`;
    },

    imageStyles() {
      const { imageContentHeight, imageContentWidth, data } = this;
      const { image } = data;
      const { hAlign: hA, vAlign: vA } = image;

      // add defaults
      const hAlign = hA || 'center';
      const vAlign = vA || 'center';

      const maxDim = Math.max(imageContentWidth, imageContentHeight);
      const rUrl = getResponsiveUrl(image.src, maxDim);

      const styles = {
        backgroundImage: `url(${rUrl})`,
        backgroundPosition: `${hAlign} ${vAlign}`,
      };

      return styles;
    },

    imageContainerClasses() {
      const classStr = this.containerClasses;
      const { image } = this.data;
      const { height: size } = image;

      const imgSize = size || 'tall';

      return `${classStr} ${imgSize}`;
    },

  },

  methods: {

    measureLayout() {
      this.imageContentHeight = this.getImageContentHeight();
      this.imageContentWidth = this.getImageContentWidth();
    },

    getImageContentHeight() {
      const el = this.$refs.imageContainer;
      return (el) ? el.clientHeight : 0;
    },

    getImageContentWidth() {
      const el = this.$refs.imageContainer;
      return (el) ? el.clientWidth : 0;
    },

  },

  watch: {

    currentWidth(/* newVal, oldVal */) {
      Vue.nextTick(() => {
        this.measureLayout();
      });
    },

  },

  mounted() {
    this.measureLayout();
  },


};
</script>


<style lang="scss" scoped>
@import '~@/styles/_mixins.scss';
@import '~@/styles/_grid.scss';

$layout--percentage: $layout--max-content-width-ng / $layout--optimal-desktop * 100%;

[data-module="work-image-text-column"] {
  width: 100%;

  // media query props
  margin-bottom: rem($layout--default-module-spacing-mobile);

  @include ngMq($nbp--tablet, up) {
    margin-bottom: rem($layout--default-module-spacing-tablet);
  }

  @include ngMq($nbp--desktop, up) {
    margin-bottom: rem($layout--default-module-spacing-desktop);
  }


  .module-layout {
    // media query props
    @include ngGrid('mobile');

    @include ngMq($nbp--tablet, up) {
      @include ngGrid('tablet');
    }

    @include ngMq($nbp--desktop, up) {
      @include ngGrid('desktop');
    }

    /*
      image container
    */

    .image-container {

      .image {
        /*
          on image style attrs:
          background-image
          background-position
        */
        overflow: hidden;
        background-size: cover;
        background-repeat: no-repeat;

        width: 100%;
      }

      .caption {
        margin-top: rem(10px);
        padding: 0 rem(20px);
      }
    }

    // image mobile

    .image-container.mobile,
    .image-container.mobile.reversed {
      grid-column: 1 / -1;
      grid-row: 1;
      margin: 0 rem($grid-padding-mobile * -1px); // pull to edges

      display: block;
    }

    .image-container.mobile.short .image {
      padding-bottom: $dimension-16-9;
    }
    .image-container.mobile.medium .image {
      padding-bottom: $dimension-4-3;
    }
    .image-container.mobile.tall .image {
      padding-bottom: $dimension-1-1;
    }


    // image tablet

    .image-container.tablet {
      grid-column: 1 / span 12;
      grid-row: 1;
      margin: 0 rem($grid-padding-tablet * -1px); // pull to edges
    }

    .image-container.tablet.reversed {
      grid-row: 2;
    }

    .image-container.tablet.short .image {
      padding-bottom: $dimension-25-9;
    }
    .image-container.tablet.medium .image {
      padding-bottom: $dimension-21-9;
    }
    .image-container.tablet.tall .image {
      padding-bottom: $dimension-16-9;
    }


    // image desktop

    .image-container.desktop {
      grid-column: 1 / span 12;
      grid-row: 1;
      margin: 0;
    }
    .image-container.desktop.reversed {
      grid-row: 2;
    }

    .image-container.desktop.short .image {
      padding-bottom: $dimension-25-9;
    }
    .image-container.desktop.medium .image {
      padding-bottom: $dimension-21-9;
    }
    .image-container.desktop.tall .image {
      padding-bottom: $dimension-16-9;
    }

    /*
      content container
    */

    .content-container {
      text-align: left;
    }

    // content mobile

    .content-container.mobile,
    .content-container.mobile.reversed {
      grid-column: 1 / -1;
      grid-row: 2;
      margin-right: 0;
      margin-left: 0;
    }

    // content tablet

    .content-container.tablet {
      grid-column: 2 / span 10;
      grid-row: 2;
    }

    .content-container.tablet.reversed {
      grid-row: 1;
    }

    // content desktop

    .content-container.desktop {
      grid-row: 2;
    }

    .content-container.desktop.reversed {
      grid-row: 1;
    }

    .content-container.desktop.left {
      grid-column: 2 / span 7;
    }
    .content-container.desktop.right {
      grid-column: 6 / span 7;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/modules/work/ImageTextColumn.vue